import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Rocket, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { useQueueJob } from "@/lib/useQueueJob";
import { PageHeader, SkeletonRows } from "@/components/app/Common";
import { useAutosave } from "@/pages/wizard/useAutosave";
import { weightsValid, subTotal } from "@/pages/wizard/jobUtils";
import { Stepper, SaveIndicator } from "@/pages/wizard/Stepper";
import { StepDescribe } from "@/pages/wizard/StepDescribe";
import { AnalysisProgress } from "@/pages/wizard/AnalysisProgress";
import { ReviewEditor } from "@/pages/wizard/ReviewEditor";
import { StagesEditor, AdvancedSettings } from "@/pages/wizard/StagesEditor";

const BLANK = { title: "", jd_text: "", department: "", location: "", employment_type: "", practice_area: "", law_firm: undefined, pqe_min: null, pqe_max: null };
const LOCAL = ["title", "jd_text", "department", "location", "employment_type", "practice_area", "law_firm", "pqe_min", "pqe_max"];

export default function JobWizard() {
  const [params, setParams] = useSearchParams();
  const draftId = params.get("draft");
  const nav = useNavigate();
  const qc = useQueryClient();
  const [job, setJob] = useState(() => (draftId ? null : BLANK));
  const [step, setStep] = useState(1);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [uploadJobId, setUploadJobId] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: serverJob, isError } = useQuery({ queryKey: ["job", draftId], queryFn: () => get(`/jobs/${draftId}`), enabled: !!draftId && !job, staleTime: 0 });

  useEffect(() => {
    if (!serverJob || job) return;
    setJob(serverJob);
    if (["queued", "processing"].includes(serverJob.analysis_status)) {
      setAnalysisJobId(serverJob.analysis_job_id);
      setStep(2);
    } else setStep(Number(params.get("step")) || serverJob.wizard_step || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverJob]);

  useEffect(() => {
    if (!draftId && job?.id) { setJob(BLANK); setStep(1); setAnalysisJobId(null); setUploadJobId(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const onCreated = useCallback((data) => {
    setJob((prev) => ({ ...data, ...Object.fromEntries(LOCAL.filter((k) => prev[k] !== undefined).map((k) => [k, prev[k]])) }));
    setParams({ draft: data.id }, { replace: true });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  }, [setParams, qc]);

  const { state: saveState, saveNow, ensureDraft } = useAutosave(job, draftId, onCreated);

  const uploadJob = useQueueJob(uploadJobId, {
    onCompleted: (d) => { setJob((j) => ({ ...j, jd_text: d.result?.text || j.jd_text })); toast.success("Text extracted. Check it, then analyse."); },
    onFailed: (d) => toast.error(d.error),
  });
  const analysisJob = useQueueJob(analysisJobId, {
    onCompleted: async () => {
      setJob(await get(`/jobs/${draftId}`));
      setAnalysisJobId(null);
      toast.success("Requirements are ready to review");
    },
    onFailed: (d) => toast.error(`Analysis failed: ${d.error}`),
  });

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const goStep = (n) => { setStep(n); setJob((j) => ({ ...j, wizard_step: n })); };

  const upload = (file) => run(async () => {
    const id = await ensureDraft();
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/jobs/${id}/jd-file`, fd);
    setJob((j) => ({ ...j, jd_file_url: data.jd_file_url, jd_file_name: data.jd_file_name }));
    setUploadJobId(data.job_id);
    toast(`Extracting text from ${file.name}…`);
  });

  const analyze = () => run(async () => {
    const id = await saveNow();
    const { data } = await api.post(`/jobs/${id}/analyze`);
    await qc.invalidateQueries({ queryKey: ["queue", data.job_id] });
    setAnalysisJobId(data.job_id);
    setJob((j) => ({ ...j, analysis_status: "queued" }));
    setStep(2);
    toast("AI analysis started");
  });

  const confirm = () => {
    const kq = job.requirements?.knockout_questions || [];
    if (kq.length < 3 || kq.length > 5) return toast.error("Use 3 to 5 knockout questions");
    if (kq.some((q) => !q.question.trim())) return toast.error("Fill in or remove empty knockout questions");
    return run(async () => {
      const id = await saveNow();
      await api.post(`/jobs/${id}/confirm-requirements`);
      setJob((j) => ({ ...j, requirements_confirmed: true, analysis_status: "confirmed", wizard_step: 3 }));
      setStep(3);
      toast.success("Requirements confirmed");
    });
  };

  const publish = () => run(async () => {
    const id = await saveNow();
    if (job.status === "draft") { await api.post(`/jobs/${id}/publish`); toast.success("Job published"); }
    else toast.success("Changes saved");
    qc.invalidateQueries();
    nav(`/app/jobs/${id}/workspace`);
  });

  const saveExit = () => run(async () => { await saveNow(); toast.success("Draft saved"); nav("/app/jobs"); });

  if (isError) return <p className="text-risk" data-testid="wizard-load-error">We couldn't load this draft.</p>;
  if (!job) return <SkeletonRows rows={6} />;

  const reachable = (n) => n === 1 || (n === 2 && job.analysis_status && job.analysis_status !== "none") || (n === 3 && job.requirements_confirmed);
  const canPublish = weightsValid(job.scoring_weights || {}) && subTotal(job.resume_subweights || {}) === 100;
  const failedQ = job.analysis_status === "failed" ? { status: "failed", error: job.analysis_error || "The analysis failed." } : null;
  const isDraft = !job.status || job.status === "draft";

  return (
    <div className="space-y-6" data-tour="pt-job-wizard" data-testid="job-wizard">
      <PageHeader title={isDraft ? "Create a job" : `Edit ${job.title}`} subtitle="Three quick steps. Nothing goes live until you publish." actions={<SaveIndicator state={saveState} />} />
      <Stepper step={step} reachable={reachable} onGo={goStep} />
      {step === 1 && <StepDescribe job={job} setJob={setJob} onUpload={upload} uploadJob={uploadJob} onAnalyze={analyze} analyzing={busy} />}
      {step === 2 && (analysisJobId ? (
        <AnalysisProgress qjob={analysisJob} onRetry={analyze} onBack={() => goStep(1)} />
      ) : ["needs_review", "confirmed"].includes(job.analysis_status) ? (
        <ReviewEditor job={job} setJob={setJob} onConfirm={confirm} confirming={busy} />
      ) : (
        <AnalysisProgress qjob={failedQ || { status: "queued", progress: 0 }} onRetry={analyze} onBack={() => goStep(1)} />
      ))}
      {step === 3 && (
        <div className="space-y-6" data-tour="pt-wizard-step-stages" data-testid="wizard-step-stages">
          <StagesEditor job={job} setJob={setJob} />
          <AdvancedSettings job={job} setJob={setJob} />
          <p className="flex items-center gap-2 text-sm text-ink-secondary" data-tour="pt-careers-slug-preview" data-testid="careers-slug-preview">
            <Globe size={16} /> Careers page: <span className="font-medium text-ink">/careers/{job.careers_page?.slug}</span>
          </p>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {isDraft && <Button variant="outline" onClick={saveExit} disabled={busy} data-tour="pt-wizard-save-exit-btn" data-testid="wizard-save-exit-btn">Save draft and exit</Button>}
            <Button onClick={publish} disabled={busy || !canPublish} data-tour="pt-wizard-publish-btn" data-testid="wizard-publish-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} {isDraft ? "Publish job" : "Save changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
